import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import axios from '../../axios';
import { Card, Button } from '../UI';
import classes from './Download.module.css';
import { downloadDevices } from '../../utils/downloadDevices';
import { parseDateToEpoch } from '../../utils/parseDateToEpoch';

const Download = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(''); 
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId === '') {
      loadAllDevices();
    } else if (selectedProjectId) {
      loadDevicesForProject(selectedProjectId);
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/projects');
      if (response.data && response.data.success && response.data.projects) {
        setProjects(response.data.projects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllDevices = async () => {
    try {
      setDevicesLoading(true);
      const response = await axios.get('/devices/organization');
      if (response.data && response.data.devices) {
        setDevices(response.data.devices);
      } else if (Array.isArray(response.data)) {
        setDevices(response.data);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const loadDevicesForProject = async (projectId) => {
    try {
      setDevicesLoading(true);
      const response = await axios.get(`/projects/${projectId}/devices`);
      if (response.data && response.data.success && response.data.devices) {
        setDevices(response.data.devices);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Failed to load devices for project:', error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleRowSelected = (state) => {
    setSelectedRows(state.selectedRows || []);
  };

  const handleDownloadSingle = (device) => {
    downloadDevices([device.id], device.projectId, device.projectName);
  };

  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) return;
    const selectedIds = selectedRows.map(row => row.id)
    downloadDevices(selectedIds, selectedProjectId, projects.find(project => project._id === selectedProjectId)?.name);
  };

  const handleSelectProject = (projectId) => {
    setSelectedRows([]);
    setSelectedProjectId(projectId);
  };

  const columns = [
    { 
      name: 'Serial number', 
      selector: row => row.serialNumber || '', 
      sortable: true, 
      style: { fontSize: '15px', whiteSpace: 'nowrap' },
      wrap: false,
      minWidth: '180px'
    },
    { 
      name: 'Device EUI', 
      selector: row => row.deviceEUI || '', 
      sortable: true, 
      style: { fontSize: '14px', whiteSpace: 'nowrap' },
      wrap: false,
      minWidth: '180px'
    },
    { 
      name: 'Device model', 
      selector: row => row.deviceModel || '', 
      sortable: true, 
      style: { fontSize: '14px' },
      wrap: true,
      minWidth: '200px'
    },
    { 
      id: 'dateAdded',
      name: 'Date added', 
      selector: row => parseDateToEpoch(row.dateAdded),
      cell: row => row.dateAdded || '',
      sortable: true, 
      style: { fontSize: '14px' },
      wrap: true,
      minWidth: '120px'
    },
    {
      name: '',
      cell: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadSingle(row);
          }}
          className={classes.downloadButton}
        >
          Download
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '120px',
      minWidth: '120px'
    }
  ];

  return (
    <div className={classes.download}>
      <div className={classes.header}>
        <h2 className={classes.title}>Download</h2>
      </div>

      {loading ? (
        <div className={classes.message}>Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className={classes.message}>No projects found</div>
      ) : (
        <Card padding="small">
          <div className={classes.projectSelector}>
            <label className={classes.label}>Select project:</label>
            <select
              value={selectedProjectId}
              onChange={(e) => handleSelectProject(e.target.value)}
              className={classes.select}
            >
              <option value="">All devices</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name} ({project.devicesCount || 0} devices)
                </option>
              ))}
            </select>
          </div>

          <div className={classes.selectedActions}>
            <Button
              variant="primary"
              disabled={selectedRows.length === 0}
              onClick={handleDownloadSelected}
              className={classes.downloadSelectedButton}
            >
              Download selected ({selectedRows.length})
            </Button>
          </div>

          <div className={classes.tableContainer}>
            <DataTable
              key={selectedProjectId}
              columns={columns}
              data={devices}
              progressPending={devicesLoading}
              noHeader={true}
              pagination={true}
              highlightOnHover={true}
              striped={true}
              selectableRows={true}
              onSelectedRowsChange={handleRowSelected}
              noDataComponent={selectedProjectId ? "No devices found in this project" : "No devices found"}
              defaultSortFieldId="dateAdded"
              defaultSortAsc={false}
              pointerOnHover={true}
              customStyles={{
                headCells: { 
                  style: { 
                    fontWeight: 'bold', 
                    fontSize: '15px',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    paddingLeft: '8px',
                    paddingRight: '8px'
                  }
                },
                cells: {
                  style: {
                    fontSize: '14px'
                  }
                }
              }}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default Download;

